import { useState, useCallback } from 'react';
import { message } from 'antd';
import { d204Api, FD140, FD141, FD142 } from '../../../services/d204Api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useD204Mode';

export function useD204Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FD140 | null>(null);
  const [formData, setFormData] = useState<Partial<FD140>>({});
  const [details, setDetails] = useState<Partial<FD141>[]>([]);
  const [lotDetails, setLotDetails] = useState<Partial<FD142>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{
    data: FD140;
    dets: Partial<FD141>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await d204Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setLotDetails(data.lot_details || []);
      setSelectedRow(0);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FD140, value: FD140[keyof FD140]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FD140): FD140[keyof FD140] => {
    return (formData as Record<string, FD140[keyof FD140]>)[key] ?? '';
  }, [formData]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof FD141): FD141[keyof FD141] => {
    const sel = details[selectedRow] as Partial<FD141> | undefined;
    return sel ? (sel as Record<string, FD141[keyof FD141]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof FD141, value: FD141[keyof FD141]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FD140) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FD140,
        details: details as FD141[],
        lot_details: lotDetails as FD142[],
      };
      if (mode === 'add') {
        const result = await d204Api.create(req);
        message.success(`新增成功: ${result.fd140_01}`);
        onSuccess(result);
      } else {
        const result = await d204Api.update(formData.fd140_01!, req);
        message.success('修改成功');
        applyResult(result);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, lotDetails, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await d204Api.delete(record.fd140_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row (FD141) */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fd141_02 || 0), 0);
    setDetails(prev => [...prev, { fd141_02: maxSeq + 1, fd141_01: formData.fd140_01 }]);
  }, [details, formData.fd140_01]);

  /** Delete the currently selected detail row */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 0) return;
    const removedSeq = details[selectedRow]?.fd141_02;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      return next.map((d, i) => ({ ...d, fd141_02: i + 1 }));
    });
    setLotDetails(prev => prev
      .filter(lot => lot.fd142_02 !== removedSeq)
      .map(lot => {
        if (removedSeq === undefined || (lot.fd142_02 || 0) < removedSeq) return lot;
        return { ...lot, fd142_02: Math.max(1, (lot.fd142_02 || 1) - 1) };
      }));
    setSelectedRow(Math.max(0, selectedRow - 1));
  }, [details, selectedRow]);

  const addLotRow = useCallback(() => {
    const detailSeq = details[selectedRow]?.fd141_02;
    if (!detailSeq) return;
    const nextBoardNo = lotDetails
      .filter(lot => lot.fd142_02 === detailSeq)
      .reduce((max, lot) => Math.max(max, lot.fd142_03 || 0), 0) + 1;
    setLotDetails(prev => [...prev, {
      fd142_01: formData.fd140_01 || '',
      fd142_02: detailSeq,
      fd142_03: nextBoardNo,
      fd142_13: '',
    }]);
  }, [details, formData.fd140_01, lotDetails, selectedRow]);

  const deleteLotRow = useCallback((lotIndex: number) => {
    const detailSeq = details[selectedRow]?.fd141_02;
    if (!detailSeq) return;
    let currentIndex = -1;
    setLotDetails(prev => prev.filter(lot => {
      if (lot.fd142_02 !== detailSeq) return true;
      currentIndex += 1;
      return currentIndex !== lotIndex;
    }));
  }, [details, selectedRow]);

  const setLotField = useCallback((lotIndex: number, key: keyof FD142, value: string) => {
    const detailSeq = details[selectedRow]?.fd141_02;
    if (!detailSeq) return;
    let currentIndex = -1;
    setLotDetails(prev => prev.map(lot => {
      if (lot.fd142_02 !== detailSeq) return lot;
      currentIndex += 1;
      return currentIndex === lotIndex ? { ...lot, [key]: value } : lot;
    }));
  }, [details, selectedRow]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
      setLotDetails(record.lot_details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fd140_36: 'NTD',
      fd140_37: 1,
      fd140_40: 1,
      fd140_80: 'N',
      fd140_67: true,
      fd140_43: user?.userId || '',
    });
    setDetails([{ fd141_02: 1 }]);
    setLotDetails([]);
    setSelectedRow(0);
  }, [user, clearFieldErrors]);

  const importSubcontractOrder = useCallback(async (orderNo: string) => {
    if (!orderNo.trim()) return;
    try { const preview=await d204Api.previewFromSubcontractOrder(orderNo.trim()); setFormData(prev=>({...prev,...preview,fd140_01:prev.fd140_01||'',fd140_11:orderNo.trim()})); setDetails(preview.details||[]); setSelectedRow(0); }
    catch(e:unknown){const err=e as {response?:{data?:{error?:string}}};message.error(err.response?.data?.error||'託工單帶入失敗');}
  },[]);

  /** Apply result after save */
  const applyResult = useCallback((result: FD140) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
    setLotDetails(result.lot_details || []);
  }, []);

  return {
    record, formData, details, lotDetails,
    selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    addLotRow, deleteLotRow, setLotField,
    revertToRecord, resetForAdd, applyResult, importSubcontractOrder,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
  };
}
